<template>
  <div ref="pageBody" class="container py-4 onboarding-page">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-xl-9">
        <div class="card mb-4 border-primary shadow-sm">
          <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h4 class="mb-0 text-white">Finish setting up your account</h4>
            <span class="badge bg-light text-primary fs-6">{{ nurseTypeLabel }}</span>
          </div>
          <div class="card-body">
            <p class="lead mb-2">
              Welcome, <strong>{{ displayName }}</strong>.
            </p>
            <p class="text-muted mb-0">
              Before you can open charts and schedules, confirm your details, upload your licence and
              malpractice insurance, and verify your state licence with Nursys.
            </p>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger" role="alert">
          {{ errorMessage }}
        </div>

        <div v-if="pendingItems.length > 0" class="alert alert-warning mb-4">
          <h6 class="alert-heading fw-bold mb-1">Still needed:</h6>
          <div class="d-flex flex-wrap gap-2">
            <span
              v-for="field in pendingItems"
              :key="field"
              class="badge bg-warning text-dark border border-secondary"
            >
              {{ field }}
            </span>
          </div>
        </div>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading your details...</span>
          </div>
        </div>

        <form v-else @submit.prevent="handleVerifyNurse">
          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-light">
              <h5 class="mb-0 text-dark">1. Your details</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="onboardingFirstName" class="form-label fw-bold">First name <span class="text-danger">*</span></label>
                  <input id="onboardingFirstName" v-model="form.first_name" type="text" class="form-control" />
                </div>
                <div class="col-md-6">
                  <label for="onboardingLastName" class="form-label fw-bold">Last name <span class="text-danger">*</span></label>
                  <input id="onboardingLastName" v-model="form.last_name" type="text" class="form-control" />
                </div>

                <div class="col-md-6">
                  <label for="onboardingPhone" class="form-label fw-bold">Phone <span class="text-danger">*</span></label>
                  <input
                    id="onboardingPhone"
                    v-model="form.phone"
                    type="tel"
                    class="form-control"
                    :class="{ 'is-invalid': phoneError }"
                    placeholder="10 digits, e.g. 5025550142"
                    @blur="validatePhone"
                  />
                  <div v-if="phoneError" class="invalid-feedback">{{ phoneError }}</div>
                </div>

                <div class="col-md-6">
                  <label for="onboardingDob" class="form-label fw-bold">Date of birth <span class="text-danger">*</span></label>
                  <input id="onboardingDob" v-model="form.date_of_birth" type="date" class="form-control" />
                </div>

                <div class="col-12">
                  <label for="onboardingAddress1" class="form-label fw-bold">Address <span class="text-danger">*</span></label>
                  <input id="onboardingAddress1" v-model="form.address_line1" type="text" class="form-control" />
                </div>
                <div class="col-12">
                  <label for="onboardingAddress2" class="form-label">Suite, unit or apartment</label>
                  <input id="onboardingAddress2" v-model="form.address_line2" type="text" class="form-control" />
                </div>
                <div class="col-md-5">
                  <label for="onboardingCity" class="form-label fw-bold">City <span class="text-danger">*</span></label>
                  <input id="onboardingCity" v-model="form.city" type="text" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label for="onboardingState" class="form-label fw-bold">State <span class="text-danger">*</span></label>
                  <select id="onboardingState" v-model="form.state" class="form-select">
                    <option value="">Select state</option>
                    <option v-for="st in statesList" :key="st.id" :value="st.id">{{ st.id }} - {{ st.name }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label for="onboardingZip" class="form-label fw-bold">Zip <span class="text-danger">*</span></label>
                  <input id="onboardingZip" v-model="form.postal_code" type="text" class="form-control" />
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 class="mb-0 text-dark">2. Your state licence</h5>
              <span v-if="nursys && nursys.last_license_status" class="badge bg-secondary">
                Nursys: {{ nursys.last_license_status }}
              </span>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-5">
                  <label for="onboardingLicNum" class="form-label fw-bold">Licence number <span class="text-danger">*</span></label>
                  <input id="onboardingLicNum" v-model="form.license_number" type="text" class="form-control" />
                </div>
                <div class="col-md-4">
                  <label for="onboardingLicState" class="form-label fw-bold">Issuing state <span class="text-danger">*</span></label>
                  <select id="onboardingLicState" v-model="form.license_state" class="form-select">
                    <option value="">Select state</option>
                    <option v-for="st in statesList" :key="st.id" :value="st.id">{{ st.id }} - {{ st.name }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label for="onboardingLicType" class="form-label fw-bold">Licence type</label>
                  <input id="onboardingLicType" :value="licenseType" type="text" class="form-control" disabled />
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-light">
              <h5 class="mb-0 text-dark">3. Licence and insurance documents</h5>
            </div>
            <div class="card-body">
              <div class="row g-4">
                <div v-for="slot in slots" :key="slot.type" class="col-md-6">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">{{ slot.label }} <span class="text-danger">*</span></h6>
                    <span v-if="documents[slot.flag]" class="badge bg-success">On file</span>
                    <span v-else class="badge bg-danger">Needed</span>
                  </div>
                  <div class="input-group mb-2">
                    <input
                      type="file"
                      class="form-control"
                      accept=".pdf,.jpg,.jpeg,.png"
                      @change="onFileSelected($event, slot.type)"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :disabled="!selectedFiles[slot.type] || uploading[slot.type]"
                      @click="uploadDoc(slot.type)"
                    >
                      <span v-if="uploading[slot.type]" class="spinner-border spinner-border-sm me-1"></span>
                      Upload
                    </button>
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="onboardingCarrier" class="form-label small fw-bold text-muted mb-1">Insurance carrier</label>
                  <input id="onboardingCarrier" v-model="form.malpractice_carrier" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-md-3">
                  <label for="onboardingInsStart" class="form-label small fw-bold text-muted mb-1">Cover starts</label>
                  <input id="onboardingInsStart" v-model="form.malpractice_starts_on" type="date" class="form-control form-control-sm" />
                </div>
                <div class="col-md-3">
                  <label for="onboardingInsEnd" class="form-label small fw-bold text-muted mb-1">Cover ends</label>
                  <input id="onboardingInsEnd" v-model="form.malpractice_ends_on" type="date" class="form-control form-control-sm" />
                </div>
                <div class="col-md-6">
                  <label for="onboardingPerIncident" class="form-label small fw-bold text-muted mb-1">Per incident limit ($)</label>
                  <input
                    id="onboardingPerIncident"
                    v-model="form.malpractice_per_incident_limit"
                    type="number"
                    min="0"
                    step="any"
                    class="form-control form-control-sm"
                  />
                </div>
                <div class="col-md-6">
                  <label for="onboardingAggregate" class="form-label small fw-bold text-muted mb-1">Annual limit ($)</label>
                  <input
                    id="onboardingAggregate"
                    v-model="form.malpractice_aggregate_limit"
                    type="number"
                    min="0"
                    step="any"
                    class="form-control form-control-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-4">
            <button type="button" class="btn btn-outline-secondary px-4" :disabled="saving || verifying" @click="saveProgress">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Save progress
            </button>

            <button type="submit" class="btn btn-primary btn-lg px-5" :disabled="!canVerify">
              <span v-if="verifying" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Verify my licence and finish
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * A nurse finishing her own registration after accepting her invitation
 * (decision D3) - main's /verifyNurse, signed in. The router guard sends a
 * user whose /me says `needs_onboarding` here and nowhere else, and the API
 * refuses her everything but /me and /me/onboarding until she is done.
 *
 * The profile half of main's page, with main's flow (utils/verifyNurseFlow.js)
 * bound to the API's /me/onboarding routes: look her licence up, enrol her
 * only when Nursys says she is not on our list, look her up again, then
 * finish. The API decides everything that lets her in - the completion looks
 * her licence up again itself and refuses main's statuses - so this page only
 * decides what she is told, and that the last four digits of her SSN are
 * asked for only after a genuine "not on the list" answer.
 *
 * SAVING NEVER ERASES. Only fields she has filled in and changed are sent, so
 * a field the page has not loaded or she has not reached yet cannot wipe what
 * is stored (the server ignores blanks too).
 */
import { useSessionStore } from '@/stores/session';
import { STATES } from '@/data/referenceLists';
import onboardingApi, { formFromState, changedFields, onboardingRefusal, isAlreadyOnboarded, SsnNotGiven } from '@/api2/onboarding';
import { keyonLookupAnswer } from '@/api2/nursysBridge';
import { runVerifyNurseFlow, UNREACHABLE } from '@/utils/verifyNurseFlow';
import { missingOnboardingFields, tenDigitPhone } from '@/utils/verifyNurseValidation';

/**
 * The API says there is nothing left for her to set up: her completion went
 * through even if its answer never reached this page. The session still says
 * she needs onboarding until it is read again, and the router guard reads the
 * session, so it is hydrated before she is taken in. True when it took her.
 */
async function leaveIfDone(vm, error) {
  if (!isAlreadyOnboarded(error)) return false;
  await vm.session.hydrate();
  vm.$router.push('/welcome');

  return true;
}

export default {
  name: 'OnboardingPage',
  data() {
    return {
      loading: true,
      saving: false,
      verifying: false,
      errorMessage: '',
      phoneError: '',
      statesList: STATES,
      form: formFromState(null),
      saved: formFromState(null),
      licenseType: '',
      documents: { has_license_file: false, has_insurance_file: false },
      nursys: null,
      serverMissing: [],
      slots: [
        { type: 'license', label: 'Your current state licence', flag: 'has_license_file' },
        { type: 'insurance', label: 'Malpractice insurance', flag: 'has_insurance_file' },
      ],
      selectedFiles: { license: null, insurance: null },
      uploading: { license: false, insurance: false },
    };
  },
  computed: {
    session() {
      return useSessionStore();
    },
    displayName() {
      return this.session.displayName || 'there';
    },
    nurseTypeLabel() {
      return this.licenseType === 'LPN' ? 'Licensed Practical Nurse' : 'Registered Nurse';
    },
    pendingItems() {
      return missingOnboardingFields(this.form, {
        hasLicenseFile: this.documents.has_license_file,
        hasInsuranceFile: this.documents.has_insurance_file,
      });
    },
    canVerify() {
      return this.pendingItems.length === 0 && !this.saving && !this.verifying && !this.loading;
    },
  },
  async created() {
    await this.fetchStatus();
  },
  methods: {
    apply(data) {
      const d = data || {};
      this.form = formFromState(d);
      this.saved = formFromState(d);
      this.licenseType = d.license_type || '';
      this.documents = {
        has_license_file: Boolean(d.documents && d.documents.has_license_file),
        has_insurance_file: Boolean(d.documents && d.documents.has_insurance_file),
      };
      this.nursys = d.nursys || null;
      this.serverMissing = Array.isArray(d.missing_fields) ? d.missing_fields : [];
    },

    async fetchStatus() {
      this.loading = true;
      this.errorMessage = '';
      try {
        this.apply(await onboardingApi.getOnboarding());
      } catch (err) {
        if (await leaveIfDone(this, err)) return;
        this.errorMessage = onboardingRefusal(err) || 'Your details could not be loaded. Please refresh the page.';
      } finally {
        this.loading = false;
      }
    },

    validatePhone() {
      if (tenDigitPhone(this.form.phone) === null) {
        this.phoneError = 'Enter a 10-digit phone number.';
        return false;
      }
      this.phoneError = '';
      return true;
    },

    onFileSelected(event, type) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null;
      this.selectedFiles[type] = file || null;
    },

    async uploadDoc(type) {
      const file = this.selectedFiles[type];
      if (!file) return;

      this.uploading[type] = true;
      this.errorMessage = '';
      try {
        await onboardingApi.uploadOnboardingDocument(file, type);
        this.selectedFiles[type] = null;
        this.documents[type === 'license' ? 'has_license_file' : 'has_insurance_file'] = true;
      } catch (err) {
        this.errorMessage = onboardingRefusal(err) || 'The file could not be uploaded. Please try again.';
      } finally {
        this.uploading[type] = false;
      }
    },

    /** Save what she changed; resolves to the server's view of her details. Throws on a refusal. */
    async persistForm() {
      const changes = changedFields(this.form, this.saved);
      if (Object.keys(changes).length === 0) return null;

      const data = await onboardingApi.updateOnboarding(changes);
      this.apply(data);

      return data;
    },

    async saveProgress() {
      this.saving = true;
      this.errorMessage = '';
      try {
        await this.persistForm();
      } catch (err) {
        this.errorMessage = onboardingRefusal(err) || 'Your details could not be saved. Please try again.';
      } finally {
        this.saving = false;
      }
    },

    /** Main's submit: save, then look up, enrol on "not on the list", look up again, finish. */
    async handleVerifyNurse() {
      if (!this.validatePhone()) return;
      const missing = this.pendingItems;
      if (missing.length > 0) {
        this.$swal({ type: 'warning', title: 'Still needed', text: missing.join(', ') });
        return;
      }

      const loader = this.$loading ? this.$loading.show({ container: this.$refs.pageBody }) : { hide: () => undefined };
      this.verifying = true;

      try {
        try {
          await this.persistForm();
        } catch (err) {
          loader.hide();
          this.$swal({ type: 'warning', title: 'Error', text: onboardingRefusal(err) || 'Your details could not be saved. Please try again.' });
          return;
        }

        await runVerifyNurseFlow(
          {
            form: {
              license: this.form.license_number,
              licenseSt: this.form.license_state,
              dob: this.form.date_of_birth || '',
              ssn: '',
              email: '',
              addr: this.form.address_line1,
              city: this.form.city,
              state: this.form.state,
              zip: this.form.postal_code,
            },
            ltype: this.licenseType,
          },
          {
            lookup: (request) => this.startLookup(request),
            add: (request) => this.startAdd(request),
            complete: (licence, controls) => this.completeNurseVerification(controls, licence),
            notify: (options) => this.$swal(options),
            hide: () => loader.hide(),
            refusal: (error) => {
              if (isAlreadyOnboarded(error)) leaveIfDone(this, error);
              return onboardingRefusal(error);
            },
          }
        );
      } finally {
        this.verifying = false;
      }
    },

    /**
     * Her own licence, looked up by the API; answered in keyon's shape, which
     * is what the flow's classifier reads. A refusal rejects, and the flow
     * says why (or "could not reach").
     */
    async startLookup() {
      return { data: keyonLookupAnswer(await onboardingApi.lookupOwnLicence()) };
    },

    /**
     * Enrol her - called by the flow only on a genuine "not on the list". The
     * last four digits of her SSN are asked for here, and only here; NCSBN's
     * refusal comes back as keyon's "Nurse Not Added" so the flow shows its
     * reason rather than blaming her details for an outage.
     */
    async startAdd(request) {
      const given = request && /^\d{4}$/.test(String(request.lastfourssn || '')) ? String(request.lastfourssn) : null;
      const ssn = given || (await this.askForSsn());
      if (!ssn) throw new SsnNotGiven();

      try {
        await onboardingApi.enrolOwnLicence(ssn);
        return { data: { status: 'Nurse Added' } };
      } catch (error) {
        if (error && error.code === 'nursys_rejected') {
          const errors = ((error.details && error.details.errors) || []).map((e) => ({ ErrorId: e.id, ErrorMessage: e.message }));
          return { data: { status: 'Nurse Not Added', errors } };
        }
        throw error;
      }
    },

    /** The four digits, or null when she cancels. Never stored on the page. */
    async askForSsn() {
      const answer = await this.$swal({
        title: 'Enrol for licence monitoring',
        text: 'Nursys does not have you on our list yet. To enrol you, NCSBN needs the last four digits of your Social Security number.',
        input: 'password',
        inputAttributes: { maxlength: 4, inputmode: 'numeric', autocomplete: 'off' },
        showCancelButton: true,
        confirmButtonText: 'Enrol',
        inputValidator: (value) => (/^\d{4}$/.test(value || '') ? undefined : 'Enter the last four digits.'),
      });
      const value = answer && answer.value ? String(answer.value) : '';

      return /^\d{4}$/.test(value) ? value : null;
    },

    /**
     * The finalize: the API verifies her licence again and finishes in one
     * step. Every arm closes the loader and tells her something.
     */
    async completeNurseVerification(controls) {
      const hide = () => (controls && typeof controls.hide === 'function' ? controls.hide() : undefined);
      try {
        await onboardingApi.completeOnboarding();
      } catch (error) {
        hide();
        if (await leaveIfDone(this, error)) return;
        await this.$swal({ type: 'warning', title: 'Error', text: onboardingRefusal(error) || UNREACHABLE });
        await this.refreshAfterRefusal();
        return;
      }

      hide();
      await this.session.hydrate();
      await this.$swal({ type: 'success', title: 'Verified', text: 'Your licence is verified and your account is ready.' });
      this.$router.push('/welcome');
    },

    /** What the server now knows about her licence (a refused status, say), without touching her form. */
    async refreshAfterRefusal() {
      try {
        const data = await onboardingApi.getOnboarding();
        this.nursys = (data && data.nursys) || null;
        this.serverMissing = data && Array.isArray(data.missing_fields) ? data.missing_fields : [];
      } catch (e) {
        /* the message she was just shown is the one that matters */
      }
    },
  },
};
</script>

<style scoped>
.onboarding-page {
  max-width: 960px;
}
</style>
